import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useCheck } from 'src/state/check-context.ts';

const ARROW_RIGHT_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>';

const ARROW_LEFT_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m12 19-7-7 7-7"/><path d="M19 12H5"/></svg>';

function loadDynamicsScript() {
  const id = 'dynamics-form-loader';
  if (document.getElementById(id)) return;
  const script = document.createElement('script');
  script.id = id;
  script.src = 'https://formui-usa1.mkt.dynamics.com/eur/FormLoader/FormLoader.bundle.js';
  document.body.appendChild(script);
}

export function useDynamicsForm(arcIdData: string | undefined) {
  const navigate = useNavigate();
  const { setArcId } = useCheck();

  useEffect(() => {
    loadDynamicsScript();

    const formDiv = document.querySelector('[data-form-id]');
    if (!formDiv) return;

    let applying = false;
    let observer: MutationObserver | null = null;

    const applyCustomizations = () => {
      if (applying) return;

      const btn = formDiv.querySelector<HTMLElement>('input[type="submit"], button[type="submit"]');
      if (!btn) return;

      applying = true;
      observer?.disconnect();

      if (btn instanceof HTMLInputElement) {
        btn.value = 'Ergebnis ansehen';
      } else {
        btn.innerHTML = `Ergebnis ansehen${ARROW_RIGHT_SVG}`;
      }

      const campaignField = formDiv.querySelector<HTMLElement>(
        '[data-targetproperty="zeb_z_marketingform_createdbycampaign"]'
      );
      if (campaignField) campaignField.style.display = 'none';

      const submitWrapper = btn.closest<HTMLElement>('[data-editorblocktype="SubmitButton"]');
      if (submitWrapper && !submitWrapper.querySelector('.dynamics-back-btn')) {
        submitWrapper.style.display = 'flex';
        submitWrapper.style.justifyContent = 'space-between';
        submitWrapper.style.gap = '12px';
        submitWrapper.style.alignItems = 'center';
        btn.style.display = '';
        btn.style.marginLeft = '';
        btn.style.marginRight = '';

        const backBtn = document.createElement('button');
        backBtn.type = 'button';
        backBtn.className = 'dynamics-back-btn';
        backBtn.innerHTML = `${ARROW_LEFT_SVG}Zurück`;
        backBtn.addEventListener('click', () => navigate('/context'));
        submitWrapper.insertBefore(backBtn, btn);
      }

      if (arcIdData) {
        const arcField = formDiv.querySelector<HTMLInputElement>(
          'input[name="zeb_mkt_archeck_id"]'
        );
        if (arcField) {
          arcField.value = arcIdData;
          arcField.readOnly = true;
          setArcId(arcIdData);
        }

        const arcLinkField = formDiv.querySelector<HTMLInputElement>(
          'input[name="zeb_mkt_arc_id"]'
        );
        if (arcLinkField) {
          arcLinkField.value = `${import.meta.env.VITE_APP_DOMAIN_NAME}/result?id=${arcIdData}`;
          const arcLinkWrapper = arcLinkField.closest<HTMLElement>(
            '[data-targetproperty="zeb_mkt_arc_id"]'
          );
          if (arcLinkWrapper) arcLinkWrapper.style.display = 'none';
        }
      }

      applying = false;
      observer?.observe(formDiv, { childList: true, subtree: true });
    };

    observer = new MutationObserver(applyCustomizations);

    const intervalId = setInterval(() => {
      const btn = formDiv.querySelector<HTMLElement>('input[type="submit"], button[type="submit"]');
      if (!btn) return;
      clearInterval(intervalId);
      applyCustomizations();
    }, 200);

    document.addEventListener('d365mkt-afterformsubmit', () => navigate('/result'), {
      once: true
    });

    return () => {
      clearInterval(intervalId);
      observer?.disconnect();
    };
  }, [navigate, setArcId, arcIdData]);
}
